import { TFunction } from "react-i18next";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { adminApi } from "../../../../../services/Api/Admin/adminApi";

class ReportPartnerUtils {
	static getSelectedColumns = (type: string) => {
		const columnsFromLocalStorage = window.localStorage.getItem(`go_report.${type}.columns`)?.split(",");
		if (columnsFromLocalStorage) {
			return columnsFromLocalStorage;
		}
		return ["provision_value", "price_paid"];
	};

	static getGroupsInfo = (t: TFunction) => {
		return {
			PROVISION_TYPE: (groupByValue: Record<string, any>) => {
				return t(`enums.provisions.type.${groupByValue.name}`, { ns: "admin" });
			},
		};
	};

	static getFilters = (t: TFunction, selected?: string[]) => {
		return [
			{
				id: "partner_type",
				name: t("common.word.partner_type", { ns: "admin" }),
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "partner",
				name: t("common.word.partner", { ns: "admin" }),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
			},
			{
				id: "date",
				name: t("lib:common.word.date"),
				type: "date",
			},
			{
				id: "transaction_at",
				name: t("modules.partner.field.transaction_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "client_name",
				name: t("lib:common.word.location"),
				type: "text",
			},
			{
				id: "client_company_name",
				name: t("common.word.company_name", { ns: "admin" }),
				type: "text",
			},
			{
				id: "client_tax_id_no",
				name: t("lib:common.word.tax_id_no.title", { ns: "lib" }),
				type: "text",
			},
			{
				id: "price_paid",
				name: t("modules.partner.field.price_paid.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "billed_period",
				name: t("common.word.billed_period", { ns: "admin" }),
				type: "date",
			},
			{
				id: "proforma_id",
				name: t("modules.partner.field.proforma_id.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "paid_at",
				name: t("modules.partner.field.paid_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "partner_tax_id_no",
				name: t("modules.partner.field.partner_tax_id_no.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "provision_value",
				name: t("common.word.provision_value", { ns: "admin" }),
				type: "number",
			},
			{
				id: "subscription_id",
				name: t("common.word.subscription", { ns: "admin" }),
				type: "text",
			},
			{
				id: "family_id",
				name: t("common.word.app", { ns: "admin" }),
				type: "text",
			},
			{
				id: "sub_partner_type",
				name: t("modules.partner.field.sub_partner_type.title", { ns: "admin" }),
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "sub_partner",
				name: t("modules.partner.field.sub_partner.title", { ns: "admin" }),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getAdminPartnersListSearchSelect(search, params, options),
				},
			},
			{
				id: "sub_partner_tax_id_no",
				name: t("modules.partner.field.sub_partner_tax_id_no.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "provision_provision",
				name: t("modules.partner.field.provision_provision.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "provision_billing_period",
				name: t("modules.partner.field.provision_billing_period.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "provision_type",
				name: t("modules.partner.field.provision_type.title", { ns: "admin" }),
				type: "list",
				options: {
					AMOUNT: t("enums.provisions.type.AMOUNT", { ns: "admin" }),
					PERCENT: t("enums.provisions.type.PERCENT", { ns: "admin" }),
				},
			},
			{
				id: "is_added_manually",
				name: t("modules.partner.field.is_added_manually.title", { ns: "admin" }),
				type: "boolean",
			},
			{
				id: "company_billed_period",
				name: t("modules.partner.field.company_billed_period.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "used_billed_period",
				name: t("modules.partner.field.used_billed_period.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "proforma_billed_period",
				name: t("modules.partner.field.proforma_billed_period.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					OPEN: "OPEN",
					CLOSED: "CLOSED",
				},
			},
			{
				id: "description",
				name: t("lib:common.word.description"),
				type: "text",
			},
		].filter((x) => selected === undefined || selected.includes(x.id));
	};

	static getColumns = (t: any) => {
		return [
			{
				id: "provision_value",
				name: t("common.word.provision_value", { ns: "admin" }),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "price_paid",
				name: t("modules.partner.field.price_paid.title", { ns: "admin" }),
				render: (item: MoneyApi) => FormatMoney(item),
			},
		];
	};

	static getGroups = (t: TFunction, selected?: string[]) => {
		return [
			{
				id: "DATE",
				name: t("lib:common.word.date"),
			},
			{
				id: "PARTNER",
				name: t("common.word.partner", { ns: "admin" }),
			},
			{
				id: "NAME",
				name: t("lib:common.word.name"),
			},
			{
				id: "CLIENT_NAME",
				name: t("lib:common.word.location"),
			},
			{
				id: "CLIENT_COMPANY_NAME",
				name: t("common.word.company_name", { ns: "admin" }),
			},
			{
				id: "CLIENT_TAX_ID_NO",
				name: t("common.word.tax_id_no.title", { ns: "lib" }),
			},
			{
				id: "BILLED_PERIOD",
				name: t("common.word.billed_period", { ns: "admin" }),
			},
			{
				id: "PAID_AT",
				name: t("modules.partner.field.paid_at.title", { ns: "admin" }),
			},
			{
				id: "TRANSACTION_DATE",
				name: t("modules.partner.field.transaction_at.title", { ns: "admin" }),
			},
			{
				id: "PARTNER_TYPE",
				name: t("common.word.partner_type", { ns: "admin" }),
			},
			{
				id: "PARTNER_TAX_ID_NO",
				name: t("modules.partner.field.partner_tax_id_no.title", { ns: "admin" }),
			},
			{
				id: "PROFORMA_ID",
				name: t("modules.partner.field.proforma_id.title", { ns: "admin" }),
			},
			{
				id: "SUBSCRIPTION_ID",
				name: t("common.word.subscription", { ns: "admin" }),
			},
			{
				id: "FAMILY_ID",
				name: t("common.word.app", { ns: "admin" }),
			},
			{
				id: "is_added_manually",
				name: t("modules.partner.field.is_added_manually.title", { ns: "admin" }),
			},
			{
				id: "SUB_PARTNER_TYPE",
				name: t("modules.partner.field.sub_partner_type.title", { ns: "admin" }),
			},
			{
				id: "SUB_PARTNER",
				name: t("modules.partner.field.sub_partner.title", { ns: "admin" }),
			},
			{
				id: "SUB_PARTNER_TAX_ID_NO",
				name: t("modules.partner.field.sub_partner_tax_id_no.title", { ns: "admin" }),
			},
			{
				id: "COMPANY_BILLED_PERIOD",
				name: t("modules.partner.field.company_billed_period.title", { ns: "admin" }),
			},
			{
				id: "PROVISION_TYPE",
				name: t("modules.partner.field.provision_type.title", { ns: "admin" }),
			},
			{
				id: "PROVISION_PROVISION",
				name: t("modules.partner.field.provision_provision.title", { ns: "admin" }),
			},
			{
				id: "PROVISION_BILLING_PERIOD",
				name: t("modules.partner.field.provision_billing_period.title", { ns: "admin" }),
			},
			{
				id: "USED_BILLED_PERIOD",
				name: t("modules.partner.field.used_billed_period.title", { ns: "admin" }),
			},
			{
				id: "PROFORMA_BILLED_PERIOD",
				name: t("modules.partner.field.proforma_billed_period.title", { ns: "admin" }),
			},
			{
				id: "DESCRIPTION",
				name: t("lib:common.word.description"),
			},
			{
				id: "LOAN_ITEM_SERIAL_NUMBERS",
				name: t("modules.partner.field.loan_item_serial_numbers.title", { ns: "admin" }),
			},
		].filter((x) => selected === undefined || selected.includes(x.id));
	};
}

export default ReportPartnerUtils;
